import { BasePlugin } from '../base-plugin';
import type {
  PluginContext,
  PageTranslateConfig,
  RevertableMutator,
  PurePageTransInsertContext,
  PurePageTranslatingContext,
} from '../../types';
import { ViewportObserver } from '../../core/viewport-observer';
import { DOMChangeDetector } from '../../core/dom-change-detector';
import {
  getTextNodes,
  filterVisibleNodes,
  getHiddenAncestors,
  applyPureTranslation,
  createTransNode,
  restorePureTranslation,
  needTranslate,
  hashString,
  normalizeWhitespace,
} from '../../utils/dom';

export class PurePagePlugin extends BasePlugin {
  name = 'pure-page';
  private config!: PageTranslateConfig;
  private initialized = false;
  private viewportObserver: ViewportObserver | null = null;
  private domChangeDetector: DOMChangeDetector | null = null;
  private translatedNodes = new Set<Text>();
  private rollbackStacks = new Map<string, Array<() => void>>();
  private nodeKeys = new WeakMap<Text, string>();
  private keySeq = 0;

  async init(ctx: PluginContext, config: PageTranslateConfig): Promise<void> {
    this.ctx = ctx;
    this.config = config;
    this.initialized = true;

    await this.waitForReady(config.translateDelay);
    await this.translateAll();

    if (config.dynamic !== false) {
      this.startDynamicDetection();
    }
  }

  private async translateAll(): Promise<void> {
    if (this.destroyed) return;
    const targets = this.getTargets();
    const excludeSelector = this.buildExcludeSelector();

    for (const target of targets) {
      const textNodes = getTextNodes(target, excludeSelector);
      const untranslated = textNodes.filter(n => !this.translatedNodes.has(n));

      if (untranslated.length === 0) continue;

      const { visible, hidden } = filterVisibleNodes(untranslated);

      if (visible.length > 0) {
        if (this.config.lazyload) {
          this.observeForLazy(visible);
        } else {
          await this.translateNodes(visible);
        }
      }

      if (hidden.length > 0) {
        this.observeHidden(hidden);
      }
    }
  }

  private observeForLazy(nodes: Text[]): void {
    if (!this.viewportObserver) {
      this.viewportObserver = this.ctx.createViewportObserver({
        offset: this.config.lazyOffset ?? 0,
      });
    }

    const grouped = this.groupByParent(nodes);

    for (const [parent, group] of grouped) {
      this.viewportObserver.observe(parent, () => {
        this.translateNodes(group);
      });
    }
  }

  private observeHidden(nodes: Text[]): void {
    const ancestors = getHiddenAncestors(nodes);
    const excludeSelector = this.buildExcludeSelector();

    if (!this.viewportObserver) {
      this.viewportObserver = this.ctx.createViewportObserver({
        offset: this.config.lazyOffset ?? 0,
      });
    }

    for (const [ancestor, group] of ancestors) {
      this.viewportObserver.observe(ancestor, () => {
        const stillValid = group.filter(n => {
          if (excludeSelector && n.parentElement?.closest(excludeSelector)) return false;
          return needTranslate(n) && !this.translatedNodes.has(n);
        });
        if (stillValid.length > 0) {
          this.translateNodes(stillValid);
        }
      });
    }
  }

  private async translateNodes(nodes: Text[]): Promise<void> {
    if (nodes.length === 0 || this.destroyed) return;

    const srcLang = this.config.srcLanguage ?? 'auto';
    const tgtLang = this.config.tgtLanguage;

    const promises = nodes.map(async node => {
      if (this.destroyed) return;
      const text = node.textContent ?? '';
      if (text.replace(/\s/g, '').length === 0) return;
      const sourceHash = this.getSourceHash(node, text);
      const mutator = this.createMutator(sourceHash);
      const onBefore = this.config.onBeforeInsertTrans;
      const onTranslatingStart = this.config.onTranslatingStart;
      const onTranslatingEnd = this.config.onTranslatingEnd;
      const anchor = node.parentNode;

      try {
        if (onTranslatingStart) {
          const startCtx: PurePageTranslatingContext = {
            textNode: node,
            sourceHash,
            anchor,
            mutator,
          };
          await Promise.resolve(onTranslatingStart(startCtx));
        }

        if (this.destroyed) return;

        const translation = await this.ctx.queue.submit(text, srcLang, tgtLang, 'text');
        if (this.destroyed) return;
        let finalTranslation = translation;
        const rules = this.config.rules;
        if (Array.isArray(rules) && rules.length > 0) {
          this.applyRules(node, rules, sourceHash);
        }

        if (onBefore) {
          const transNode = createTransNode();
          transNode.textContent = translation;
          const beforeCtx: PurePageTransInsertContext = {
            textNode: node,
            translatedText: translation,
            transNode,
            anchor,
            sourceHash,
            mutator,
          };
          const maybeNode = await Promise.resolve(onBefore(beforeCtx));
          const edited = maybeNode instanceof HTMLElement ? maybeNode : transNode;
          finalTranslation = edited.textContent ?? translation;
        }

        if (node.parentNode && !this.translatedNodes.has(node)) {
          this.pauseDetector();
          applyPureTranslation(node, finalTranslation);
          this.translatedNodes.add(node);
          this.resumeDetector();
        }
      } catch (e) {
        console.error('[PurePagePlugin] Translation failed for node:', e);
      } finally {
        if (onTranslatingEnd) {
          const endCtx: PurePageTranslatingContext = {
            textNode: node,
            sourceHash,
            anchor,
            mutator,
          };
          await Promise.resolve(onTranslatingEnd(endCtx));
        }
      }
    });

    await Promise.allSettled(promises);
  }

  private startDynamicDetection(): void {
    const targets = this.getTargets();
    const excludeSelector = this.buildExcludeSelector();

    for (const target of targets) {
      this.domChangeDetector = this.ctx.createDOMChangeDetector({
        target,
        ignoreSelector: excludeSelector
          ? `[alitranslate="no"], ${excludeSelector}`
          : '[alitranslate="no"]',
        debounceMs: 300,
        onDetect: (records) => this.handleDOMChanges(records),
      });
      this.domChangeDetector.start();
    }
  }

  private handleDOMChanges(records: MutationRecord[]): void {
    if (this.destroyed) return;
    const newNodes: Text[] = [];
    const excludeSelector = this.buildExcludeSelector();

    for (const record of records) {
      if (record.type === 'childList') {
        for (const added of Array.from(record.addedNodes)) {
          if (added.nodeType === Node.TEXT_NODE) {
            const text = added as Text;
            if (excludeSelector && text.parentElement?.closest(excludeSelector)) continue;
            if (needTranslate(text) && !this.translatedNodes.has(text)) {
              newNodes.push(text);
            }
          } else if (added.nodeType === Node.ELEMENT_NODE) {
            const texts = getTextNodes(added, excludeSelector);
            for (const t of texts) {
              if (!this.translatedNodes.has(t)) {
                newNodes.push(t);
              }
            }
          }
        }
      } else if (record.type === 'characterData') {
        const text = record.target as Text;
        if (excludeSelector && text.parentElement?.closest(excludeSelector)) continue;
        if (needTranslate(text)) {
          this.rollbackBySourceHash(this.getSourceHash(text, text.textContent ?? ''));
          this.translatedNodes.delete(text);
          (text as any).translated = false;
          newNodes.push(text);
        }
      }
    }

    if (newNodes.length > 0) {
      const { visible, hidden } = filterVisibleNodes(newNodes);

      if (visible.length > 0) {
        if (this.config.lazyload) {
          this.observeForLazy(visible);
        } else {
          this.translateNodes(visible);
        }
      }

      if (hidden.length > 0) {
        this.observeHidden(hidden);
      }
    }
  }

  private pauseDetector(): void {
    this.domChangeDetector?.pause();
  }

  private resumeDetector(): void {
    this.domChangeDetector?.resume();
  }

  private getTargets(): Element[] {
    const selectors = this.config.targetSelectors ?? ['body'];
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    const excludeSelectors = this.config.excludeSelectors ?? [];
    const excludeList = Array.isArray(excludeSelectors) ? excludeSelectors : [excludeSelectors];

    const targets: Element[] = [];
    for (const selector of selectorList) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        const isExcluded = excludeList.some(ex => el.matches(ex));
        if (!isExcluded) targets.push(el);
      });
    }

    return targets.length > 0 ? targets : [document.body];
  }

  /** 合并 excludeSelectors，供 getTextNodes 等按子树排除 */
  private buildExcludeSelector(): string | undefined {
    const excludes = this.config.excludeSelectors;
    const excludeList = excludes ? (Array.isArray(excludes) ? excludes : [excludes]) : [];
    return [...excludeList].filter(Boolean).join(', ') || undefined;
  }

  private groupByParent(nodes: Text[]): Map<Element, Text[]> {
    const map = new Map<Element, Text[]>();
    for (const node of nodes) {
      const parent = node.parentElement ?? document.body;
      const group = map.get(parent) ?? [];
      group.push(node);
      map.set(parent, group);
    }
    return map;
  }

  destroy(): void {
    this.destroyed = true;
    this.viewportObserver?.destroy();
    this.viewportObserver = null;
    this.domChangeDetector?.destroy();
    this.domChangeDetector = null;

    this.remove();
  }

  remove(): void {
    if (!this.initialized) return;
    this.viewportObserver?.destroy();
    this.viewportObserver = null;
    this.domChangeDetector?.destroy();
    this.domChangeDetector = null;
    const targets = this.getTargets();
    for (const target of targets) {
      restorePureTranslation(target);
    }

    this.translatedNodes.clear();
    this.rollbackAll();
  }

  private applyRules(
    textNode: Text,
    rules: NonNullable<PageTranslateConfig['rules']>,
    sourceHash: string,
  ): void {
    let start: Element | null = textNode.parentElement;
    for (const rule of rules) {
      let el: Element | null = start;
      while (el) {
        if (el.matches(rule.selector)) {
          if (rule.style) {
            const container = el as HTMLElement;
            for (const [key, value] of Object.entries(rule.style)) {
              this.trackStyleRollback(sourceHash, container, key);
              container.style.setProperty(key, value);
            }
          }
          if (rule.className) {
            this.trackClassRollback(sourceHash, el as HTMLElement, rule.className);
            (el as HTMLElement).classList.add(rule.className);
          }
          break;
        }
        el = el.parentElement;
      }
    }
  }

  private getSourceHash(node: Text, text: string): string {
    const existing = this.nodeKeys.get(node);
    if (existing) return existing;
    const key = `pure-${++this.keySeq}-${hashString(normalizeWhitespace(text))}`;
    this.nodeKeys.set(node, key);
    return key;
  }

  private createMutator(sourceHash: string): RevertableMutator {
    return {
      setStyle: (el, prop, value, priority) => {
        this.trackStyleRollback(sourceHash, el, prop);
        el.style.setProperty(prop, value, priority ?? '');
      },
      addClass: (el, className) => {
        this.trackClassRollback(sourceHash, el, className);
        el.classList.add(className);
      },
      removeClass: (el, className) => {
        const hadClass = el.classList.contains(className);
        this.pushRollback(sourceHash, () => {
          if (hadClass) {
            el.classList.add(className);
          } else {
            el.classList.remove(className);
          }
        });
        el.classList.remove(className);
      },
      appendNode: (parent, node) => {
        const nextSibling = node.nextSibling;
        parent.appendChild(node);
        if (node.nodeType === Node.ELEMENT_NODE) {
          (node as Element).setAttribute('alitranslate', 'no');
        }
        this.pushRollback(sourceHash, () => {
          if (node.parentNode === parent) {
            parent.removeChild(node);
          } else if (nextSibling) {
            parent.insertBefore(node, nextSibling);
          } else {
            parent.appendChild(node);
          }
        });
      },
      removeNode: (node) => {
        const parent = node.parentNode;
        const nextSibling = node.nextSibling;
        if (!parent) return;
        parent.removeChild(node);
        this.pushRollback(sourceHash, () => {
          if (nextSibling && nextSibling.parentNode === parent) {
            parent.insertBefore(node, nextSibling);
          } else {
            parent.appendChild(node);
          }
        });
      },
    };
  }

  private pushRollback(sourceHash: string, fn: () => void): void {
    const stack = this.rollbackStacks.get(sourceHash) ?? [];
    stack.push(fn);
    this.rollbackStacks.set(sourceHash, stack);
  }

  private trackStyleRollback(sourceHash: string, el: HTMLElement, prop: string): void {
    const prevValue = el.style.getPropertyValue(prop);
    const prevPriority = el.style.getPropertyPriority(prop);
    const hadInline = prevValue !== '';
    this.pushRollback(sourceHash, () => {
      if (!hadInline) {
        el.style.removeProperty(prop);
        return;
      }
      el.style.setProperty(prop, prevValue, prevPriority);
    });
  }

  private trackClassRollback(sourceHash: string, el: HTMLElement, className: string): void {
    const hadClass = el.classList.contains(className);
    this.pushRollback(sourceHash, () => {
      if (!hadClass) {
        el.classList.remove(className);
      }
    });
  }

  private rollbackBySourceHash(sourceHash: string): void {
    const stack = this.rollbackStacks.get(sourceHash);
    if (!stack || stack.length === 0) return;
    for (let i = stack.length - 1; i >= 0; i--) {
      try {
        stack[i]();
      } catch (e) {
        console.error('[PurePagePlugin] rollback failed:', e);
      }
    }
    this.rollbackStacks.delete(sourceHash);
  }

  private rollbackAll(): void {
    const keys = Array.from(this.rollbackStacks.keys());
    for (const key of keys) {
      this.rollbackBySourceHash(key);
    }
  }
}
